import { useState, useEffect } from 'react'
import { useAppStore } from '@/store/useAppStore'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { motion } from 'framer-motion'
import { ListTodo, Users, Zap } from 'lucide-react'

const STEPS = [
  {
    icon: Zap,
    title: 'Welcome to PRJCT',
    desc: "The fastest way to manage your team's work. Let's get you set up in 30 seconds.",
  },
  {
    icon: ListTodo,
    title: 'Create your first project',
    desc: 'Projects organize your tasks into focused workspaces. Name one now or skip and add later.',
    field: 'projectName',
    placeholder: 'e.g. ZORTS // BLSSM',
  },
  {
    icon: Users,
    title: "You're all set",
    desc: 'Your project is ready. Invite teammates, add tasks, and start shipping.',
  },
]

const SEEN_KEY = 'prjct-onboarding-v2-seen'

export function OnboardingWelcome() {
  const projects = useAppStore((s) => s.projects)
  const lastSyncAt = useAppStore((s) => s._lastSyncAt)
  const addProject = useAppStore((s) => s.addProject)
  const [step, setStep] = useState(0)
  const [projectName, setProjectName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(() => !!localStorage.getItem(SEEN_KEY))

  // Auto-mark onboarding done for returning users whose projects loaded after auth.
  // Persist to localStorage so it sticks across reloads.
  useEffect(() => {
    if (!done && projects.length > 0) {
      localStorage.setItem(SEEN_KEY, '1')
      setDone(true)
    }
  }, [projects.length, done])

  // Render-time guard: bail on the same render that projects populate, so we never
  // flash the modal in the one-frame window between data arriving and useEffect running.
  // Also wait for the first sync — until then we don't know whether the user has projects.
  if (done || lastSyncAt === 0 || projects.length > 0) return null

  function finish() {
    localStorage.setItem(SEEN_KEY, '1')
    window.dispatchEvent(new CustomEvent('prjct:welcome-done'))
    setDone(true)
  }

  async function next() {
    if (submitting) return
    setSubmitting(true)
    try {
      if (step === 1 && projectName.trim()) {
        await addProject({ name: projectName.trim() })
      }
      if (step >= STEPS.length - 1) {
        finish()
        return
      }
      setStep((s) => s + 1)
    } finally {
      setSubmitting(false)
    }
  }

  const s = STEPS[step]
  const Icon = s.icon
  const isProjectStep = s.field === 'projectName'
  const ctaLabel = step >= STEPS.length - 1
    ? 'Get started'
    : isProjectStep && !projectName.trim()
      ? 'Skip →'
      : 'Next →'

  return (
    <Modal open title="" onClose={finish} size="sm">
      <div className="flex flex-col items-center text-center gap-5 py-2">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col items-center gap-4 w-full"
        >
          <div className="w-12 h-12 rounded-2xl bg-accent/10 flex items-center justify-center">
            <Icon size={22} className="text-accent" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-text">{s.title}</h2>
            <p className="text-sm text-text-muted mt-1">{s.desc}</p>
          </div>
          {isProjectStep && (
            <Input
              autoFocus
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder={s.placeholder}
              onKeyDown={(e) => { if (e.key === 'Enter' && !submitting) next() }}
              disabled={submitting}
              className="w-full"
            />
          )}
        </motion.div>

        <div className="flex items-center gap-3 w-full">
          <div className="flex gap-1 flex-1">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-accent' : 'bg-border'}`} />
            ))}
          </div>
          <Button variant="primary" onClick={next} disabled={submitting}>
            {submitting ? 'Working…' : ctaLabel}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
